import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { ShieldOff } from '@/components/icons';
import type { OrgAction, PlatformAction } from '@1qa/shared';
import { useAuth } from '@/lib/auth';
import { tokenStore } from '@/lib/api';
import { EmptyState, PageLoader } from '@/components/ui';

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <PageLoader label="Signing you in…" />;
  if (!user || !tokenStore.get()) {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }
  return <>{children}</>;
}

/** Renders a friendly "no access" state instead of the page when the user lacks permission. */
export function RequirePermission({
  org,
  platform,
  children,
}: {
  org?: OrgAction;
  platform?: PlatformAction;
  children: ReactNode;
}) {
  const auth = useAuth();
  const allowed = (org ? auth.can(org) : true) && (platform ? auth.canPlatform(platform) : true);
  if (!allowed) {
    const isAdmin = auth.user?.platformRole === 'ADMIN';
    return (
      <EmptyState
        icon={<ShieldOff className="size-6" />}
        title="You don't have access to this page"
        description={
          isAdmin && org
            ? 'Admins are not part of any organization or project. Use the Administration pages instead.'
            : 'Ask a Project Admin of your organization to give you the right role.'
        }
      />
    );
  }
  return <>{children}</>;
}
